import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { useRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';
import ProvisioningStyles from 'pages/provisioning/internal-app-gateway-provisioning/style';
import {
    environmentDetails,
    orderId,
    sspSubmitOrder,
    toggleSspModels,
    isValidAppGWPrivateIPAddress,
    isCostcenterValid,
} from 'pages/provisioning/store/internal-app-gateway-provisioning';
import {
    resetEnvironmentDetails,
    resetOrder,
    resetToggleSspModal,
    formOrderObjFromOrderDetails,
} from 'pages/provisioning/internal-app-gateway-provisioning/utils';

const ProvisioningHeader = React.lazy(() => import('pages/provisioning/internal-app-gateway-provisioning/ProvisioningHeader'));
const SspForm = React.lazy(() => import('pages/provisioning/internal-app-gateway-provisioning/SspForm'));
const OrderSuccessDialog = React.lazy(() => import('pages/provisioning/internal-app-gateway-provisioning/OrderSuccessDialog'));
const ShowCostDetails = React.lazy(() => import('pages/provisioning/internal-app-gateway-provisioning/ShowCostDetails'));

const NewInternalAppGateWayProvisioning = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [order, setOrder] = useRecoilState(sspSubmitOrder);
    const [, resetOrderId] = useRecoilState(orderId);
    const [, resetEnvDetails] = useRecoilState(environmentDetails);
    const [, resetToggleSspModels] = useRecoilState(toggleSspModels);
    const [, setIsCostcenterValid] = useRecoilState(isCostcenterValid);
    const [, setValidAppGWPrivateIPAddress] = useRecoilState(isValidAppGWPrivateIPAddress);
    const { catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const axios = useAxios();

    async function getCosts() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}/costs`);

        return data;
    }
    async function checkCatalog() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}`);
        const costs = await getCosts();
        if (cartItemId && operation && catalogServiceId) {
            const { data: orderDetails } = await axios.get(`${endPoints.order.orderDetails}/${cartItemId}`);
            const orderObj = formOrderObjFromOrderDetails(catalogServiceId, orderDetails);
            // const uploadObj = formUploadObjFromOrderDetails(catalogServiceId, orderDetails);
            setIsCostcenterValid(true);
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                emptyCost: costs,
            }));
            // setUploadedFiles(uploadObj);
        } else {
            setOrder((previous) => ({
                ...previous,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data?.cloudService,
                validationStatus: 'clearValidation',
                isTouched: false,
                emptyCost: costs,
            }));
        }
        setLoading(false);
    }

    const reset = () => {
        setOrder({ ...order, ...resetOrder });
        resetOrderId('');
        resetEnvDetails(resetEnvironmentDetails);
        resetToggleSspModels(resetToggleSspModal);
        setValidAppGWPrivateIPAddress(false);
    };

    React.useEffect(() => {
        if (catalogCode) {
            setLoading(true);
            reset();
            checkCatalog();
        }
    }, [catalogCode]);

    /* eslint-disable arrow-body-style */
    React.useEffect(() => {
        return () => reset();
        // eslint-disable-next-line
    }, []);
    return (
        <>
            <Helmet>
                <title>Application Gateway URL Onboarding</title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                }
            >
                {isLoading && (
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                )}
                {!isLoading && (
                    <Box sx={ProvisioningStyles.provisioningBox}>
                        <ProvisioningHeader />
                        <Box sx={ProvisioningStyles.sspform_wrapper} data-testid="index-form" className="contentContainer">
                            <SspForm />
                        </Box>
                        <br />
                        <OrderSuccessDialog />
                        <ShowCostDetails />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default NewInternalAppGateWayProvisioning;
